<template>
  <div />
</template>

<script>

/**
 * Vue: Stylitics recommendations (stylitics-recommendations)
 * -----------------------------------------------------------------------------
 * Uses Stylitics to create product recommendations in an image format via
 * product ID.
 * - https://docs.stylitics.com/classic/default
 *
 * @param {String} productId - product ID that Stylitics will use to query data.
 * @param {String} liquid.sectionId - Stylitics container ID.
 *
 */
import { nextTick } from 'vue'
import classes from '~/config/classes'
import { waitFor } from '~/helpers/general'

export default {
  name: 'StyliticsRecommendations',

  props: {
    iconTarget: {
      type: String,
      default: '',
    },
    maximumOutfits: {
      type: Number,
      default: 3,
    },
    minimumOutfits: {
      type: Number,
      default: 6,
    },
    productId: {
      type: String,
      default: '',
    },
    sectionId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      styliticsWidgetContainer: null,
    }
  },

  computed: {

    /**
     * Compute locale.
     * @returns {String}
     */
    locale() {
      return `${cnvs.store.language}-${cnvs.store.country}`
    },
  },

  mounted() {
    waitFor('StyliticsHotspotsWidget', () => {
      nextTick().then(() => {
        this.styliticsWidgetContainer = document.getElementById(this.sectionId)
        this.initialiseWidget()
      })
    })
  },

  methods: {

    /**
     * Initialise the Stylitics widget.
     */
    initialiseWidget() {
      if (!this.styliticsWidgetContainer) {
        return
      }

      // eslint-disable-next-line no-undef
      const widget = new StyliticsHotspotsWidget('childsplay', this.sectionId, {
        api: {
          item_number: this.productId,
          max: this.maximumOutfits,
          min: this.minimumOutfits,
        },

        customer: { locale: this.locale },

        /**
         * Breakpoints are based on Stylitics widget breakpoints, NOT screen
         * size.
         */
        responsive: [
          [0, { columns: 1 }],
          [640, { columns: 2 }],
          [1024, { columns: 3 }],
          [1280, { columns: 3 }],
          [1920, { columns: 4 }],
        ],

        /**
         * All Numbers represent pixel values.
         */
        display: {
          bundleBackgroundColor: getComputedStyle(document.documentElement)
            .getPropertyValue('--color-grey'),
          bundleProductList: 'product-list-on-click',
          clickableCarouselGutterWidth: 4,
          clickableCarouselNextItemPeek: 16,
          swipeableCarouselDots: true,
          swipeableCarouselGutterWidth: 4,
          swipeableCarouselNextItemPeek: 16,
        },
      })

      widget.on('mount', 'bundles', (data) => {
        if (data.bundles && data.bundles.length < this.minimumOutfits) {
          this.hideStyliticsWidget()
          return
        }

        this.hideLoadingElements()
      })

      widget.on('mount', 'bundle', (data) => {
        this.replaceBundleCta(data.element)
        this.setEventListeners(data.element)
      })

      widget.start()
    },

    /**
     * Remove loading cards.
     * - Can't use Canvas' 'getComponentActive()' here.
     */
    hideLoadingElements() {
      document.querySelector('[js-stylitics="loading-container"]').style.display = 'none'
    },

    /**
     * Remove Stylitics Widget if the number of outfits is less than the set
     * minimum value.
     */
    hideStyliticsWidget() {
      document.querySelector(
        '[data-component-handle="stylitics-recommendations"]',
      ).style.display = 'none'
    },

    /**
     * Replaces the 'View the look' default CTA with a target icon. Replaces
     * its aria-label with a visually hidden span tag to follow our guidelines.
     *
     * @param {HtmlElement} element - data.element from Stylitics data
     */
    replaceBundleCta(element) {
      const bundleCta = element.querySelector(
        '.stylitics-cta[role="button"]',
      )

      /**
       * If Stylitics outfit expander button is not present, assume that there
       * is an issue with Stylitics' code and prevent any of our code from
       * running.
       */
      if (!bundleCta) {
        return
      }

      const bundleAccessibleLabel = bundleCta.getAttribute('aria-label')

      bundleCta.textContent = ''
      const bundleSpan = document.createElement('span')
      bundleCta.insertAdjacentElement('afterbegin', bundleSpan)

      bundleCta.removeAttribute('aria-label')
      bundleSpan.textContent = bundleAccessibleLabel ? bundleAccessibleLabel : 'View target'
      bundleSpan.classList.add(classes.hidden)
      bundleCta.insertAdjacentHTML('beforeend', this.iconTarget)
    },

    /**
     * Set event listeners.
     *
     * @param {HtmlElement} element - data.element from Stylitics data
     */
    setEventListeners(element) {
      const bundleCta = element.querySelector(
        '.stylitics-cta[role="button"]',
      )

      bundleCta.addEventListener('click', (event) => {
        document.querySelector('.stylitics-close').addEventListener('click', () => {
          event.target.parentNode.setAttribute('aria-expanded', 'false')
        })

        event.target.parentNode.setAttribute('aria-expanded', 'true')
      })
    },
  },
}
</script>

<style lang="scss">
@import './stylitics-recommendations';
</style>
